/**
 * PATH: apps/quote/src/lib/quote-builder-math/computeLine.ts
 * FILE_ID: QUOTE-BUILDER-MATH-COMPUTE-001
 * PURPOSE: Pure live-compute of line-item derived fields (hrs/days/rate/subtotal/margin).
 * WHY: Estimators expect Hrs/Days/Calc Rate cells to fill in AS THEY TYPE. The
 *      backend recalc round-trip is debounced (400ms) + queued. Without
 *      frontend optimism the grid feels broken — user reports "formulas are
 *      all missing" because the cells show "—" until the backend catches up.
 *
 * FLOW:
 *   ┌──────────────────┐    ┌────────────────┐    ┌──────────────────┐
 *   │  Line input      │ →  │  computeLine   │ →  │  Line totals     │
 *   │ qty, pmpd, L/M/  │    │  (pure)        │    │ hrs, days, rate, │
 *   │ P/S charges, AR  │    │                │    │ subtotal, margin │
 *   └──────────────────┘    └────────────────┘    └──────────────────┘
 *
 * MIRRORS BACKEND: report/services/quote_calculation* — when backend math
 *   diverges, this module is the front-line and the source of truth for what
 *   the user sees while typing. Backend reconciles on save.
 *
 * DEPENDENCIES:
 *   - none (pure math)
 * RELATED:
 *   - QUOTE-BUILDER-MATH-FORMULA-001 (formula.ts — =expr support, MR2.5)
 *   - apps/quote/src/components/builder/scope-sheet-grid/LineItemRow.tsx
 *   - apps/quote/src/components/builder/phantom-row/usePhantomRowLogic.ts
 */

/** Hours per workday — matches the SAM/Bravada spreadsheet convention. */
export const HOURS_PER_DAY = 8

/**
 * Anything that can be coerced to a number (string from API JSON, number from
 * local state, null/undefined when blank). Returns 0 on non-finite input so the
 * downstream math degrades to "no contribution" rather than NaN.
 */
function toNum(v: unknown): number {
  if (v === null || v === undefined) return 0
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0
  const s = String(v).trim()
  if (!s) return 0
  const n = Number.parseFloat(s)
  return Number.isFinite(n) ? n : 0
}

/** Inputs accepted by computeLine. All optional — missing fields treated as 0. */
export interface ComputeLineInput {
  quantity?: string | number | null
  productivity_pmpd?: string | number | null
  labour_chargeout?: string | number | null
  labour_cost?: string | number | null
  material_chargeout?: string | number | null
  material_cost?: string | number | null
  plant_chargeout?: string | number | null
  plant_cost?: string | number | null
  subcon_chargeout?: string | number | null
  subcon_cost?: string | number | null
  /** Operator's manual rate-per-unit override. If > 0, takes precedence over computed rate_per_unit. */
  adjusted_rate?: string | number | null
}

/** Computed values returned by computeLine. */
export interface ComputeLineOutput {
  /** Days = qty / pmpd (when pmpd > 0, else 0). */
  labour_days: number
  /** Hours = days * hoursPerDay (opts.hoursPerDay, default HOURS_PER_DAY). */
  labour_hours: number
  /** Calculated $/unit = labour_chargeout / qty (when qty > 0, else 0). */
  rate_per_unit: number
  /** Effective $/unit honouring the override: adjusted_rate if > 0, else rate_per_unit. */
  effective_rate_per_unit: number
  /** Subtotal of all chargeouts (L + M + P + S). */
  chargeout_total: number
  /** Subtotal of all cost fields (L + M + P + S costs). */
  total_cost: number
  /** Margin in dollars = chargeout_total - total_cost. */
  margin: number
  /** Margin % = margin / chargeout_total * 100, 0 when chargeout_total <= 0. */
  margin_percent: number
}

/** Optional compute knobs. The tenant rate-profile (overlay seam, Track 2.1a) is
 *  injected HERE at the call boundary rather than read as a global, so this
 *  function stays pure. `hoursPerDay` defaults to {@link HOURS_PER_DAY}. */
export interface ComputeLineOptions {
  /** Working hours per day for the days→hours conversion. Default: HOURS_PER_DAY (8). */
  hoursPerDay?: number
}

/**
 * Compute derived line-item fields from inputs.
 *
 * Pure: no side effects, no globals, no I/O. Safe to call on every render.
 * Tenant rate-profile differences (e.g. a non-8 working day) are passed in via
 * `opts` — the canonical engine takes them as an explicit input, never reads a
 * tenant global (overlay-seam contract, Track 2.1a).
 *
 * @example
 *   computeLine({ quantity: 100, productivity_pmpd: 25, labour_chargeout: 5000 })
 *   // → { labour_days: 4, labour_hours: 32, rate_per_unit: 50, ... }  (8h day)
 *   computeLine({ quantity: 100, productivity_pmpd: 25 }, { hoursPerDay: 7.6 })
 *   // → { labour_days: 4, labour_hours: 30.4, ... }
 */
export function computeLine(input: ComputeLineInput, opts?: ComputeLineOptions): ComputeLineOutput {
  const qty = toNum(input.quantity)
  const pmpd = toNum(input.productivity_pmpd)
  const labourCO = toNum(input.labour_chargeout)
  const materialCO = toNum(input.material_chargeout)
  const plantCO = toNum(input.plant_chargeout)
  const subconCO = toNum(input.subcon_chargeout)
  const labourCost = toNum(input.labour_cost)
  const materialCost = toNum(input.material_cost)
  const plantCost = toNum(input.plant_cost)
  const subconCost = toNum(input.subcon_cost)
  const adjustedRate = toNum(input.adjusted_rate)
  const hoursPerDay = opts?.hoursPerDay ?? HOURS_PER_DAY

  const labour_days = pmpd > 0 ? qty / pmpd : 0
  const labour_hours = labour_days * hoursPerDay
  const rate_per_unit = qty > 0 ? labourCO / qty : 0
  const effective_rate_per_unit = adjustedRate > 0 ? adjustedRate : rate_per_unit

  const chargeout_total = labourCO + materialCO + plantCO + subconCO
  const total_cost = labourCost + materialCost + plantCost + subconCost
  const margin = chargeout_total - total_cost
  const margin_percent = chargeout_total > 0 ? (margin / chargeout_total) * 100 : 0

  return {
    labour_days,
    labour_hours,
    rate_per_unit,
    effective_rate_per_unit,
    chargeout_total,
    total_cost,
    margin,
    margin_percent,
  }
}

/**
 * Resolve the "best" value for a derived field: prefer the live-computed
 * frontend value when ANY input that drives it is set; otherwise fall back to
 * the backend's stored value. This is what lets the cells start showing
 * computed values immediately without nuking the backend's authoritative
 * post-save totals when the user hasn't touched anything.
 */
export function pickComputed<K extends keyof ComputeLineOutput>(
  computed: ComputeLineOutput,
  storedValue: unknown,
  field: K,
  hasFrontendInput: boolean,
): number {
  if (hasFrontendInput) return computed[field] as number
  const stored = toNum(storedValue)
  return stored !== 0 ? stored : (computed[field] as number)
}
